import React, { useState, useEffect } from 'react';
import { FiShield, FiEdit2, FiTrash2, FiPlus, FiSave, FiX, FiEye, FiEdit, FiTrash, FiUserMinus } from 'react-icons/fi';
import { toast } from 'react-toastify';
import AxiosInstance from '../../AxiosInstance';
import config from '../../config/frontend.config';
import { usePermissions } from '../../contexts/PermissionContext';
import CustomDropdown from '../../components/CustomDropdown';
import DeleteModal from '../../components/DeleteModal';
import './RoleManagement.css';

const RoleManagement = () => {
  const { hasPermission } = usePermissions();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [userCompanyName, setUserCompanyName] = useState('');
  const [userCompanyId, setUserCompanyId] = useState('');
  const [formData, setFormData] = useState({
    RoleName: '',
    RoleDisplayName: '',
    Description: '',
    Permissions: {}
  });

  // Define all available permissions with their categories
  const permissionCategories = [
    {
      name: 'User Management',
      permissions: [
        { key: 'users', label: 'Users' },
        { key: 'team', label: 'Team Members' },
        { key: 'roles', label: 'Roles' },
        { key: 'invitations', label: 'Invitations' }
      ]
    },
    {
      name: 'Content Management',
      permissions: [
        { key: 'templates', label: 'Templates' },
        { key: 'projects', label: 'Projects' },
        { key: 'library', label: 'Library' },
        { key: 'campaigns', label: 'Email Campaigns' }
      ]
    },
    {
      name: 'Analytics & Reports',
      permissions: [
        { key: 'analytics', label: 'Analytics' },
        { key: 'activity', label: 'Activity Logs' },
        { key: 'reports', label: 'Reports' }
      ]
    },
    {
      name: 'System Settings',
      permissions: [
        { key: 'settings', label: 'Settings' },
        { key: 'system', label: 'System Settings' },
      ]
    }
  ];

  // Permission types
  const permissionTypes = [
    { key: 'view', label: 'View', icon: FiEye, color: '#3b82f6' },
    { key: 'create', label: 'Create', icon: FiPlus, color: '#10b981' },
    { key: 'edit', label: 'Edit', icon: FiEdit, color: '#f59e0b' },
    { key: 'delete', label: 'Delete', icon: FiTrash, color: '#ef4444' }
  ];


  // Initialize permissions structure
  const initializePermissions = () => {
    const initialPermissions = {};
    permissionCategories.forEach(category => {
      category.permissions.forEach(permission => {
        initialPermissions[permission.key] = {
          view: false,
          create: false,
          edit: false,
          delete: false
        };
      });
    });
    return initialPermissions;
  };

  // Transform API permissions to frontend format
  const transformApiPermissionsToFrontend = (apiPermissions) => {
    const frontendPermissions = initializePermissions();
    
    if (!apiPermissions) return frontendPermissions;
    
    // Map API permissions to frontend structure
    const permissionMapping = {
      // User Management
      users: {
        view: 'canViewUsers',
        create: 'canManageUsers',
        edit: 'canManageUsers', 
        delete: 'canDeleteUsers'
      },
      team: {
        view: 'canViewTeamMembers',
        create: 'canRemoveTeamMembers',
        edit: 'canEditTeamMembers',
        delete: 'canDeleteTeamMembers'
      },
      roles: {
        view: 'canUpdateRoles',
        create: 'canUpdateRoles',
        edit: 'canUpdateRoles',
        delete: 'canUpdateRoles'
      },
      invitations: {
        view: 'canViewInvitations',
        create: 'canCreateInvitation',
        edit: 'canEditInvitations',
        delete: 'canCancelInvitations'
      },
      // Content Management
      templates: {
        view: 'canViewTemplates',
        create: 'canManageTemplates',
        edit: 'canManageTemplates',
        delete: 'canManageTemplates'
      },
      projects: {
        view: 'canViewProjects',
        create: 'canCreateProjects',
        edit: 'canEditProjects',
        delete: 'canDeleteProjects'
      },
      library: {
        view: 'canViewLibrary',
        create: 'canManageLibrary',
        edit: 'canManageLibrary',
        delete: 'canManageLibrary'
      },
      campaigns: {
        view: 'canViewEmailCampaigns',
        create: 'canCreateEmailCampaigns',
        edit: 'canEditEmailCampaigns',
        delete: 'canDeleteEmailCampaigns'
      },
      // Analytics & Reports
      analytics: {
        view: 'canViewAnalytics',
        create: 'canViewAnalytics',
        edit: 'canViewAnalytics',
        delete: 'canViewAnalytics'
      },
      activity: {
        view: 'canViewActivity',
        create: 'canViewActivity',
        edit: 'canViewActivity',
        delete: 'canViewActivity'
      },
      reports: {
        view: 'canViewAnalytics', // Using analytics permission for reports
        create: 'canViewAnalytics',
        edit: 'canViewAnalytics',
        delete: 'canViewAnalytics'
      },
      // System Settings
      settings: {
        view: 'canViewSettings',
        create: 'canManageSettings',
        edit: 'canManageSettings',
        delete: 'canManageSettings'
      },
      system: {
        view: 'canViewSystemSettings',
        create: 'canViewSystemSettings',
        edit: 'canViewSystemSettings',
        delete: 'canViewSystemSettings'
      },
      admin: {
        view: 'canAccessAdminPanel',
        create: 'canAccessAdminPanel',
        edit: 'canAccessAdminPanel',
        delete: 'canAccessAdminPanel'
      }
    };
    
    // Transform the permissions
    Object.keys(permissionMapping).forEach(permissionKey => {
      // Check if the permission key exists in frontendPermissions
      if (!frontendPermissions[permissionKey]) {
        console.warn(`Permission key "${permissionKey}" not found in frontendPermissions, initializing...`);
        frontendPermissions[permissionKey] = {
          view: false,
          create: false,
          edit: false,
          delete: false
        };
      }
      
      const mapping = permissionMapping[permissionKey];
      Object.keys(mapping).forEach(action => {
        const apiPermissionKey = mapping[action];
        if (frontendPermissions[permissionKey]) {
          frontendPermissions[permissionKey][action] = apiPermissions[apiPermissionKey] || false;
        }
      });
    });
    
    // Special handling for email campaigns send permission
    // If canSendEmailCampaigns is true, also set create to true (since send requires create)
    if (apiPermissions.canSendEmailCampaigns && !frontendPermissions.campaigns.create) {
      frontendPermissions.campaigns.create = true;
    }
    
    return frontendPermissions;
  };

  // Transform frontend permissions back to API format
  const transformFrontendPermissionsToApi = (frontendPermissions) => {
    const apiPermissions = {};
    
    // Map frontend permissions back to API structure
    const permissionMapping = {
      // User Management
      users: {
        view: 'canViewUsers',
        create: 'canManageUsers',
        edit: 'canManageUsers', 
        delete: 'canDeleteUsers'
      },
      team: {
        view: 'canViewTeamMembers',
        create: 'canRemoveTeamMembers',
        edit: 'canEditTeamMembers',
        delete: 'canDeleteTeamMembers'
      },
      roles: {
        view: 'canUpdateRoles',
        create: 'canUpdateRoles',
        edit: 'canUpdateRoles',
        delete: 'canUpdateRoles'
      },
      invitations: {
        view: 'canViewInvitations',
        create: 'canCreateInvitation',
        edit: 'canEditInvitations',
        delete: 'canCancelInvitations'
      },
      // Content Management
      templates: {
        view: 'canViewTemplates',
        create: 'canManageTemplates',
        edit: 'canManageTemplates',
        delete: 'canManageTemplates'
      },
      projects: {
        view: 'canViewProjects',
        create: 'canCreateProjects',
        edit: 'canEditProjects',
        delete: 'canDeleteProjects'
      },
      library: {
        view: 'canViewLibrary',
        create: 'canManageLibrary',
        edit: 'canManageLibrary',
        delete: 'canManageLibrary'
      },
      campaigns: {
        view: 'canViewEmailCampaigns',
        create: 'canCreateEmailCampaigns',
        edit: 'canEditEmailCampaigns',
        delete: 'canDeleteEmailCampaigns'
      },
      // Analytics & Reports
      analytics: {
        view: 'canViewAnalytics',
        create: 'canViewAnalytics',
        edit: 'canViewAnalytics',
        delete: 'canViewAnalytics'
      },
      activity: {
        view: 'canViewActivity',
        create: 'canViewActivity',
        edit: 'canViewActivity',
        delete: 'canViewActivity'
      },
      reports: {
        view: 'canViewAnalytics',
        create: 'canViewAnalytics',
        edit: 'canViewAnalytics',
        delete: 'canViewAnalytics'
      },
      // System Settings
      settings: {
        view: 'canViewSettings',
        create: 'canManageSettings',
        edit: 'canManageSettings',
        delete: 'canManageSettings'
      },
      system: {
        view: 'canViewSystemSettings',
        create: 'canViewSystemSettings',
        edit: 'canViewSystemSettings',
        delete: 'canViewSystemSettings'
      },
      admin: {
        view: 'canAccessAdminPanel',
        create: 'canAccessAdminPanel',
        edit: 'canAccessAdminPanel',
        delete: 'canAccessAdminPanel'
      }
    };
    
    // Transform the permissions
    Object.keys(permissionMapping).forEach(permissionKey => {
      const mapping = permissionMapping[permissionKey];
      Object.keys(mapping).forEach(action => {
        const apiPermissionKey = mapping[action];
        apiPermissions[apiPermissionKey] = frontendPermissions[permissionKey]?.[action] || false;
      });
    });
    
    // Special handling for email campaigns send permission
    // Send permission is tied to create permission - if user can create, they can send
    if (frontendPermissions.campaigns?.create) {
      apiPermissions.canSendEmailCampaigns = true;
    } else {
      apiPermissions.canSendEmailCampaigns = false;
    }
    
    return apiPermissions;
  };

  const fetchRoles = async () => {
    try {
      setLoading(true);
      
      // Build API URL with companyId query param for superadmin
      let apiUrl = '/role/all';
      if (isSuperAdmin && selectedCompanyId) {
        apiUrl += `?companyId=${selectedCompanyId}`;
      }
      
      // Add timestamp to prevent caching
      const timestamp = new Date().getTime();
      apiUrl += apiUrl.includes('?') ? `&_t=${timestamp}` : `?_t=${timestamp}`;
      
      const response = await AxiosInstance.get(apiUrl);
      
      // Check if response is successful (statusCode 200 or just data exists)
      if (response && response.data) {
        // Handle both response formats
        let rolesData = null;
        
        if (response.data.statusCode === 200 && response.data.data) {
          rolesData = response.data.data;
        } else if (Array.isArray(response.data)) {
          rolesData = response.data;
        } else if (Array.isArray(response.data.data)) {
          rolesData = response.data.data;
        }
        
        // Ensure it's an array
        rolesData = Array.isArray(rolesData) ? rolesData : [];
        
        // For normal users, filter to show ONLY their company's roles (not system roles)
        if (!isSuperAdmin && userCompanyId) {
          rolesData = rolesData.filter(role => 
            role.CompanyId === userCompanyId
          );
        }
        
        setRoles(rolesData);
        
        // If no roles found and user is not superadmin, try to create default roles
        if (rolesData.length === 0 && !isSuperAdmin && userCompanyId) {
          
          // Try to trigger default roles creation via API
          try {
            // This will be handled by backend - we just need to ensure roles exist
            // The backend should auto-create default roles when user is created
            // But if they don't exist, we can show a message
            toast.warning('No roles found. Please contact administrator or create a role manually.');
          } catch (err) {
            console.error('Error creating default roles:', err);
          }
        }
        
        // Set admin role as selected by default (only if no role is currently selected)
        if (rolesData.length > 0 && !selectedRole) {
          const adminRole = rolesData.find(role => 
            role.RoleName?.toLowerCase() === 'admin' || 
            role.RoleDisplayName?.toLowerCase() === 'admin'
          );
          
          if (adminRole) {
            setSelectedRole(adminRole);
            setPermissions(transformApiPermissionsToFrontend(adminRole.Permissions));
            // Don't set selectedCompanyId here - it's already set by user selection
          } else {
            // If no admin role found, select first role
            setSelectedRole(rolesData[0]);
            setPermissions(transformApiPermissionsToFrontend(rolesData[0].Permissions));
            // Don't set selectedCompanyId here - it's already set by user selection
          }
        } else if (rolesData.length === 0) {
          setSelectedRole(null);
          setPermissions({});
        }
      } else {
        setRoles([]);
        // Only show error if there's actually an error message
        if (response.data && response.data.message && response.data.statusCode !== 200) {
          toast.error(response.data.message || 'Failed to fetch roles');
        }
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data
      });
      
      // Don't show error toast here as AxiosInstance interceptor will handle it
      // Only log the error for debugging
      setRoles([]);
    } finally {
      setLoading(false);
    }
  };

  // Check if user is superadmin and get company info
  useEffect(() => {
    const userData = config.getUserData();
    const userRole = config.getUserRole();
    const isSuperAdminUser = userRole === 'SuperAdmin' || userData?.Role === 'SuperAdmin' || userData?.isSuperAdmin;
    setIsSuperAdmin(isSuperAdminUser);
    
    if (!isSuperAdminUser && userData) {
      // For normal users, get their company info
      setUserCompanyName(userData.CompanyName || '');
      // We'll need to fetch CompanyId from API
      fetchUserCompanyId();
    }
  }, []);

  // Fetch user's CompanyId
  const fetchUserCompanyId = async () => {
    try {
      const userId = config.getUserId();
      if (userId) {
        const response = await AxiosInstance.get(`/user/userdata?UserId=${userId}`);
        if (response?.data?.statusCode === 200 && response.data.data) {
          const companyId = response.data.data.CompanyId || '';
          setUserCompanyId(companyId);
          setSelectedCompanyId(companyId);
          setFormData(prev => ({ ...prev, CompanyId: companyId }));
        }
      }
    } catch (error) {
      console.error('Error fetching user company:', error);
    }
  };

  // Fetch companies with admin users (for superadmin)
  const fetchCompanies = async () => {
    if (!isSuperAdmin) return;
    
    try {
      setLoadingCompanies(true);
      const response = await AxiosInstance.get('/user/companies-with-admins');
      
      if (response?.data?.statusCode === 200) {
        const companiesData = response.data.data || [];
        
        // Set companies state first
        setCompanies(companiesData);
        
        // Set first company as default only if no company is currently selected
        if (companiesData.length > 0 && !selectedCompanyId) {
          const firstCompanyId = companiesData[0].CompanyId;
          setSelectedCompanyId(firstCompanyId);
          // CompanyId is handled separately via selectedCompanyId state
        }
      } else {
        setCompanies([]);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      setCompanies([]);
    } finally {
      setLoadingCompanies(false);
    }
  };

  useEffect(() => {
    if (isSuperAdmin) {
      fetchCompanies();
    } else {
      fetchUserCompanyId();
    }
  }, [isSuperAdmin]);


  useEffect(() => {
    // Only fetch roles if company is selected (for superadmin) or if userCompanyId is set (for normal users)
    if (isSuperAdmin) {
      if (selectedCompanyId) {
        fetchRoles();
      }
    } else {
      // Wait for userCompanyId to be set before fetching roles
      if (userCompanyId) {
        fetchRoles();
      }
    }
    // Removed companies.length from dependencies to prevent unnecessary re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId, isSuperAdmin, userCompanyId]);

  // Handle role selection from dropdown
  const handleRoleSelect = (option) => {
    const role = option.data; // Get the full role data from the option
    setSelectedRole(role);
    setPermissions(transformApiPermissionsToFrontend(role.Permissions));
    // For superadmin, update selectedCompanyId if role belongs to a different company
    // Only update if the company is different to avoid unnecessary re-renders
    if (isSuperAdmin && role.CompanyId && role.CompanyId !== selectedCompanyId) {
      setSelectedCompanyId(role.CompanyId);
    }
  };

  // Handle permission toggle
  const handlePermissionToggle = (permissionKey, permissionType) => {
    setPermissions(prev => ({
      ...prev,
      [permissionKey]: {
        ...prev[permissionKey],
        [permissionType]: !prev[permissionKey][permissionType]
      }
    }));
  };

  // Save permissions for selected role
  const handleSavePermissions = async () => {
    if (!selectedRole) {
      toast.error('Please select a role first');
      return;
    }

    try {
      const apiPermissions = transformFrontendPermissionsToApi(permissions);
      
      const updateData = {
          ...selectedRole,
          Permissions: apiPermissions
      };
      
      // For superadmin, include CompanyId if changed
      if (isSuperAdmin && selectedCompanyId) {
        updateData.CompanyId = selectedCompanyId;
        }
      
      const response = await AxiosInstance.put(
        `/role/update/${selectedRole.RoleId}`,
        updateData
      );

      if (response.data && response.data.statusCode === 200) {
        toast.success('Permissions updated successfully!');
        fetchRoles(); // Refresh roles to get updated data
      } else {
        toast.error(response.data?.message || 'Failed to update permissions');
      }
    } catch (error) {
      console.error('Error updating permissions:', error);
      toast.error('Failed to update permissions');
    }
  };

  // Create new role
  const handleCreateRole = async (e) => {
    e.preventDefault();
    
    if (!formData.RoleName || !formData.RoleDisplayName) {
      toast.error('Role name and display name are required');
      return;
    }

    // Determine CompanyId based on user type
    let companyId = null;
    if (isSuperAdmin) {
      // Use selected company from main page dropdown
      companyId = selectedCompanyId || null;
      if (!companyId) {
        toast.error('Please select a company first');
        return;
      }
    } else {
      // Use user's company
      companyId = userCompanyId || null;
      if (!companyId) {
        toast.error('Company information not found');
        return;
      }
    }

    try {
      const roleData = {
        ...formData,
        CompanyId: companyId,
        Permissions: transformFrontendPermissionsToApi(initializePermissions())
      };

      const response = await AxiosInstance.post('/role/create', roleData);
      
      if (response.data && response.data.statusCode === 201) {
        toast.success('Role created successfully!');
        setShowRoleModal(false);
        resetForm();
        fetchRoles();
      } else {
        toast.error(response.data?.message || 'Failed to create role');
      }
    } catch (error) {
      console.error('Error creating role:', error);
      toast.error('Failed to create role');
    }
  };


  const resetForm = () => {
    setFormData({
      RoleName: '',
      RoleDisplayName: '',
      Description: '',
      Permissions: {}
    });
  };

  // Update role
  const handleUpdateRole = async (e) => {
    e.preventDefault();
    
    if (!formData.RoleName || !formData.RoleDisplayName) {
      toast.error('Role name and display name are required');
      return;
    }

    // For update, keep the role's existing CompanyId
    // Don't change company when updating a role

    try {
      const updateData = {
          ...selectedRole,
          RoleName: formData.RoleName,
          RoleDisplayName: formData.RoleDisplayName,
          Description: formData.Description
      };

      const response = await AxiosInstance.put(
        `/role/update/${selectedRole.RoleId}`,
        updateData
      );
      
      if (response.data && response.data.statusCode === 200) {
        toast.success('Role updated successfully!');
        setShowRoleModal(false);
        resetForm();
        fetchRoles();
      } else {
        toast.error(response.data?.message || 'Failed to update role');
      }
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error('Failed to update role');
    }
  };

  // Delete role
  const handleDeleteRole = async () => {
    if (!selectedRole) {
      toast.error('No role selected');
      return;
    }

    try {
      const response = await AxiosInstance.delete(
        `/role/delete/${selectedRole.RoleId}`
      );
      
      if (response.data && response.data.statusCode === 200) {
        toast.success('Role deleted successfully!');
        setSelectedRole(null);
        setPermissions({});
        fetchRoles();
      } else {
        toast.error(response.data?.message || 'Failed to delete role');
      }
    } catch (error) {
      console.error('Error deleting role:', error);
      toast.error('Failed to delete role');
    }
  };

  // Open delete modal
  const openDeleteModal = () => {
    if (!selectedRole) {
      toast.error('No role selected');
      return;
    }
    setShowDeleteModal(true);
  };

  // Convert roles to dropdown options
  const roleOptions = roles.map(role => ({
    value: role.RoleId,
    label: role.RoleDisplayName || role.RoleName,
    data: role // Include the full role data
  }));
  

  if (loading) {
    return (
      <div className="role-management">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading roles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="role-management">
      <div className="role-header">
        <div className="role-header-left">
          <FiShield className="role-icon" />
          <h1>Role Management</h1>
        </div>
        <button 
          className="create-role-btn"
          onClick={() => {
            setIsEditMode(false);
            resetForm();
            setShowRoleModal(true);
          }}
        >
          <FiPlus />
          Create New Role
        </button>
      </div>

      <div className="role-content">
        {/* Role Selection */}
        <div className="role-selection-section">
          <div className="section-header">
            <h2>Select Role / Company</h2>
            <p>Choose a role to manage its permissions</p>
          </div>
          
          {/* Company and Role Dropdowns Side by Side */}
          <div className="dropdowns-row" style={{ display: 'flex', gap: '20px', alignItems: 'flex-end' }}>
            {/* Company Selection/Display */}
            {(isSuperAdmin || userCompanyName) && (
              <div className="company-selection-inline" style={{ flex: '0 0 auto', minWidth: '300px', maxWidth: '350px' }}>
                
                {isSuperAdmin ? (
                  <div className="company-dropdown-container">
                    {loadingCompanies ? (
                      <div style={{ padding: '10px', textAlign: 'center', color: '#6b7280' }}>
                        Loading companies...
                      </div>
                    ) : (
                      <CustomDropdown
                        options={companies.map(company => ({
                          value: company.CompanyId,
                          label: `${company.CompanyName} (${company.AdminUsers?.length || 0} admin${company.AdminUsers?.length !== 1 ? 's' : ''})`,
                          data: company
                        }))}
                        value={selectedCompanyId ? {
                          value: selectedCompanyId,
                          label: companies.find(c => c.CompanyId === selectedCompanyId)?.CompanyName || 'Selected Company'
                        } : null}
                        onChange={(option) => {
                          const companyId = option?.value || '';
                          // Only update if different to prevent unnecessary re-renders
                          if (companyId !== selectedCompanyId) {
                            setSelectedCompanyId(companyId);
                            // Reset selected role when company changes
                            setSelectedRole(null);
                            setPermissions({});
                            // fetchRoles will be called automatically by useEffect when selectedCompanyId changes
                          }
                        }}
                        placeholder="-- Select Company --"
                        className="company-dropdown"
                        searchable={true}
                        textColor="white"
                      />
                    )}
                  </div>
                ) : (
                  <div className="company-display">
                    <div style={{
                      padding: '12px 16px',
                      backgroundColor: '#f3f4f6',
                      borderRadius: '6px',
                      border: '1px solid #e5e7eb',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#374151'
                    }}>
                      {userCompanyName || 'No company assigned'}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Role Dropdown */}
            <div className="role-dropdown-container" style={{ flex: '0 0 auto', minWidth: '300px', maxWidth: '350px' }}>
            <CustomDropdown
              options={roleOptions}
              value={selectedRole ? { value: selectedRole.RoleId, label: selectedRole.RoleDisplayName || selectedRole.RoleName } : null}
              onChange={handleRoleSelect}
              placeholder="Select a role..."
              className="role-dropdown"
              searchable={true}
              textColor="white"
            />
            </div>
          </div>
            
            {selectedRole && (
              <div className="selected-role-info">
                <div className="role-details">
                  <h3>{selectedRole.RoleDisplayName || selectedRole.RoleName}</h3>
                  <p>{selectedRole.Description || 'No description available'}</p>
                  </div>
                  <div className="role-actions">
                      <button 
                    className="edit-role-btn"
                    onClick={() => {
                      setFormData({
                        RoleName: selectedRole.RoleName,
                        RoleDisplayName: selectedRole.RoleDisplayName,
                        Description: selectedRole.Description || '',
                        Permissions: selectedRole.Permissions || {}
                      });
                      setIsEditMode(true);
                      setShowRoleModal(true);
                    }}
                      >
                        <FiEdit2 />
                    Edit Role
                      </button>
                      <button 
                    className="delete-role-btn"
                    onClick={openDeleteModal}
                      >
                        <FiTrash2 />
                    Delete
                      </button>
                </div>
            </div>
          )}
        </div>
      </div>

        {/* Permission Matrix */}
        {selectedRole && (
          <div className="permission-matrix-section">
            <div className="section-header">
              <h2>Permission Matrix</h2>
              <p>Configure permissions for {selectedRole.RoleDisplayName || selectedRole.RoleName}</p>
              {isSuperAdmin && selectedCompanyId && (
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>
                  Company: {companies.find(c => c.CompanyId === selectedCompanyId)?.CompanyName || 'Selected Company'}
                </div>
              )}
              {!isSuperAdmin && userCompanyName && (
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>
                  Company: {userCompanyName}
                </div>
              )}
            </div>

            <div className="permission-matrix">
              <div className="matrix-header">
                <div className="permission-name-header">Permission</div>
                {permissionTypes.map(type => (
                  <div key={type.key} className="permission-type-header">
                    <type.icon style={{ color: type.color }} />
                    <span>{type.label}</span>
                  </div>
                ))}
              </div>

              {permissionCategories.map(category => (
                <div key={category.name} className="permission-category">
                  <div className="category-header">
                    <h4>{category.name}</h4>
                  </div>
                  
                  {category.permissions.map(permission => (
                    <div key={permission.key} className="permission-row">
                      <div className="permission-name">
                        <span>{permission.label}</span>
                      </div>
                      
                      {permissionTypes.map(type => {
                        // For team's create permission, use Remove icon
                        const isTeamCreate = permission.key === 'team' && type.key === 'create';
                        const displayIcon = isTeamCreate ? FiUserMinus : type.icon;
                        return (
                          <div key={type.key} className="permission-cell">
                            <button
                              className={`permission-toggle ${permissions[permission.key]?.[type.key] ? 'active' : ''}`}
                              onClick={() => handlePermissionToggle(permission.key, type.key)}
                              style={{
                                backgroundColor: permissions[permission.key]?.[type.key] ? type.color : '#f1f5f9',
                                borderColor: type.color
                              }}
                            >
                              {permissions[permission.key]?.[type.key] && (
                                React.createElement(displayIcon, { style: { color: 'white' } })
                              )}
                            </button>
                          </div>
                        );
                      })}
              </div>
                  ))}
                </div>
              ))}
              </div>

            <div className="permission-actions">
              <button 
                className="save-permissions-btn"
                onClick={handleSavePermissions}
              >
                  <FiSave />
                Save Permissions
                </button>
              </div>
          </div>
        )}

      {/* Unified Role Modal (Create/Edit) */}
      {showRoleModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{isEditMode ? 'Edit Role' : 'Create New Role'}</h3>
              <button 
                className="modal-close"
                onClick={() => setShowRoleModal(false)}
              >
                <FiX />
              </button>
            </div>
            
            <div className="role-form">
              <form onSubmit={isEditMode ? handleUpdateRole : handleCreateRole}>

              <div className="form-group">
                  <label>Role Name *</label>
                <input
                  type="text"
                  value={formData.RoleName}
                  onChange={(e) => setFormData({...formData, RoleName: e.target.value})}
                    placeholder={isEditMode ? "Enter role name (e.g., admin, manager)" : "e.g., content_manager"}
                  required
                />
              </div>
                
              <div className="form-group">
                  <label>Display Name *</label>
                <input
                  type="text"
                  value={formData.RoleDisplayName}
                  onChange={(e) => setFormData({...formData, RoleDisplayName: e.target.value})}
                    placeholder={isEditMode ? "Enter display name (e.g., Administrator, Manager)" : "e.g., Content Manager"}
                  required
                />
              </div>
                
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.Description}
                  onChange={(e) => setFormData({...formData, Description: e.target.value})}
                    placeholder="Describe the role's responsibilities..."
                    rows={3}
                  />
                </div>
                
                <div className="form-actions" style={{display: 'flex', justifyContent: 'flex-end', gap: '1rem', padding: '0', background: 'transparent', borderTop: 'none'}}>
                  <button 
                    type="button" 
                    className="cancel-btn"
                    onClick={() => setShowRoleModal(false)}
                    style={{padding: '0.75rem 1.5rem', background: '#f3f4f6', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem'}}
                  >
                  Cancel
                </button>
                  <button 
                    type="submit" 
                    className="update-btn"
                    style={{padding: '0.75rem 1.5rem', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem'}}
                  >
                    {isEditMode ? <FiSave /> : <FiPlus />}
                    {isEditMode ? 'Update Role' : 'Create Role'}
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Role Modal */}
      <DeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteRole}
        title="Role"
        description="This action cannot be undone."
        itemName={selectedRole?.RoleDisplayName || selectedRole?.RoleName}
        icon={FiShield}
        confirmText="DELETE"
        cancelText="CANCEL"
        requireTextConfirmation={true}
        confirmationText="delete"
      />
    </div>
  );
};

export default RoleManagement; 

